import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Eye, Download, Printer, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PDFPreviewModal - Modal component for previewing PDFs inline
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {string} documentType - Type of document: 'quote', 'invoice', 'credit-note', 'payment-complement', 'purchase-order'
 * @param {string} documentId - ID of the document to preview
 * @param {string} documentNumber - Display number/name of the document (for title)
 * @param {string} token - Auth token for API calls
 */
export const PDFPreviewModal = ({ 
  open, 
  onClose, 
  documentType, 
  documentId, 
  documentNumber,
  token 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Map document types to API endpoints
  const endpointMap = {
    'quote': 'preview/quote',
    'invoice': 'preview/invoice',
    'credit-note': 'preview/credit-note',
    'payment-complement': 'preview/payment-complement',
    'purchase-order': 'preview/purchase-order'
  };

  // Map document types to display names
  const titleMap = {
    'quote': 'Cotización',
    'invoice': 'Factura',
    'credit-note': 'Nota de Crédito',
    'payment-complement': 'Complemento de Pago',
    'purchase-order': 'Orden de Compra'
  };

  useEffect(() => {
    if (open && documentId && documentType) {
      loadPDF();
    }
    
    return () => {
      // Cleanup blob URL when component unmounts or modal closes
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [open, documentId, documentType]);

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    
    const endpoint = endpointMap[documentType];
    if (!endpoint) {
      setError('Tipo de documento no soportado');
      setLoading(false);
      return;
    }

    try {
      // Debug: log token status
      console.log('PDFPreview - Token present:', !!token, 'Token length:', token?.length);
      
      const response = await fetch(`${API_URL}/api/${endpoint}/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Error ${response.status}`);
      }

      // Get the PDF as a blob
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError(err.message || 'Error al cargar el PDF');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (pdfUrl) {
      // Open PDF in new window and trigger print
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    }
  };

  const handleDownload = async () => {
    if (!pdfUrl) return;
    
    // Create a temporary link to download
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${titleMap[documentType] || 'documento'}_${documentNumber || documentId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Vista Previa: {titleMap[documentType] || 'Documento'} {documentNumber && `- ${documentNumber}`}
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              disabled={loading || error}
              data-testid="preview-print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={loading || error}
              data-testid="preview-download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              data-testid="preview-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden bg-gray-100">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
                <p className="mt-2 text-gray-600">Cargando vista previa...</p>
              </div>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-red-600">
                <p className="font-medium">Error al cargar el documento</p>
                <p className="text-sm mt-1">{error}</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={loadPDF}
                >
                  Reintentar
                </Button>
              </div>
            </div>
          )}
          
          {!loading && !error && pdfUrl && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title={`Vista previa de ${titleMap[documentType] || 'documento'}`}
              data-testid="pdf-preview-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * PreviewButton - Reusable button to open PDF preview
 */
export const PreviewButton = ({ 
  documentType, 
  documentId, 
  documentNumber,
  token,
  variant = "ghost",
  size = "icon",
  className = ""
}) => {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setShowPreview(true)}
        className={className}
        title="Vista previa"
        data-testid={`preview-btn-${documentId}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      
      <PDFPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        documentType={documentType}
        documentId={documentId}
        documentNumber={documentNumber}
        token={token}
      />
    </>
  );
};

export default PDFPreviewModal;
